import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiTrash2, FiSave, FiAlertCircle } from 'react-icons/fi';
import { orderBy } from 'firebase/firestore';
import { Card, Button, Input, Badge, Modal } from '../../components/UI';
import { useFirestoreCollection } from '../../hooks';
import { writeAdminConfig, deleteAdminConfig } from '../../services/adminService';
import styles from './AppSettings.module.css';

// appSettings/{settingId} — admin write per firestore.rules.
// Each doc: { value, type, description?, updatedAt }
// Types: 'boolean' | 'string' | 'number' | 'json'

const TYPES = ['boolean', 'string', 'number', 'json'];

// Curated list of conventional keys the mobile app reads.
// Picking one of these makes the flag work end-to-end without code changes
// in the app — the mobile reads them by exact string id.
const SUGGESTED_KEYS = [
  { key: 'maintenanceMode', type: 'boolean', defaultValue: 'false', description: 'Show "we are doing maintenance" screen instead of the app.' },
  { key: 'signupEnabled', type: 'boolean', defaultValue: 'true', description: 'Allow new users to register. Set false to pause signups.' },
  { key: 'forceUpdateMinVersion', type: 'string', defaultValue: '', description: 'Minimum allowed app version (semver). Older clients see "please update".' },
  { key: 'announcementBanner', type: 'string', defaultValue: '', description: 'Top-of-feed banner text. Empty string hides it.' },
  { key: 'aiHealthScanEnabled', type: 'boolean', defaultValue: 'true', description: 'Master switch for the AI pet-health scan feature.' },
  { key: 'mateMatchingEnabled', type: 'boolean', defaultValue: 'true', description: 'Master switch for the mate matching feature.' },
  { key: 'storeEnabled', type: 'boolean', defaultValue: 'true', description: 'Master switch for the marketplace store.' },
  { key: 'subscriptionsEnabled', type: 'boolean', defaultValue: 'true', description: 'Master switch for paid subscription plans.' },
  { key: 'reviewsEnabled', type: 'boolean', defaultValue: 'true', description: 'Allow users to leave reviews on businesses & vets.' },
  { key: 'pushNotificationsEnabled', type: 'boolean', defaultValue: 'true', description: 'Global push toggle (overrides per-user pref when off).' },
  { key: 'maxFreeAiScans', type: 'number', defaultValue: '5', description: 'AI scans allowed per non-subscriber per month.' },
  { key: 'supportEmail', type: 'string', defaultValue: 'support@dierend.com', description: 'Email shown in the in-app "Contact Us".' },
  { key: 'termsUrl', type: 'string', defaultValue: 'https://dierend.com/terms-of-service.html', description: 'Latest Terms of Service URL.' },
  { key: 'privacyUrl', type: 'string', defaultValue: 'https://dierend.com/privacy-policy.html', description: 'Latest Privacy Policy URL.' },
  { key: 'rolloutFlags', type: 'json', defaultValue: '{"newFeed": false, "betaChat": false}', description: 'JSON map for staged feature rollouts.' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const valueDisplay = (setting) => {
  if (setting.type === 'boolean') return setting.value ? 'true' : 'false';
  if (setting.type === 'json') {
    try {
      return typeof setting.value === 'string' ? setting.value : JSON.stringify(setting.value);
    } catch {
      return '(invalid json)';
    }
  }
  return String(setting.value ?? '');
};

const blankDraft = { id: '', type: 'boolean', value: 'false', description: '' };

const AppSettings = () => {
  const constraints = useMemo(() => [orderBy('__name__')], []);
  const { data: settings, loading, error } = useFirestoreCollection('appSettings', constraints);

  const [showEdit, setShowEdit] = useState(false);
  const [draft, setDraft] = useState(blankDraft);
  const [editingId, setEditingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {}, []);

  const openCreate = () => {
    setDraft(blankDraft);
    setEditingId(null);
    setErrorMsg('');
    setShowEdit(true);
  };

  const applySuggestion = (suggestion) => {
    setDraft({
      id: suggestion.key,
      type: suggestion.type,
      value: suggestion.defaultValue,
      description: suggestion.description,
    });
  };

  const existingKeys = new Set(settings.map((s) => s.id));
  const availableSuggestions = SUGGESTED_KEYS.filter((s) => !existingKeys.has(s.key));

  const openEdit = (setting) => {
    setDraft({
      id: setting.id,
      type: setting.type || 'string',
      value:
        setting.type === 'json'
          ? typeof setting.value === 'string'
            ? setting.value
            : JSON.stringify(setting.value, null, 2)
          : String(setting.value ?? ''),
      description: setting.description || '',
    });
    setEditingId(setting.id);
    setErrorMsg('');
    setShowEdit(true);
  };

  const parseValue = (rawValue, type) => {
    if (type === 'boolean') return rawValue === true || rawValue === 'true';
    if (type === 'number') {
      const n = Number(rawValue);
      if (isNaN(n)) throw new Error('Value must be a number');
      return n;
    }
    if (type === 'json') {
      return JSON.parse(rawValue);
    }
    return String(rawValue);
  };

  const handleSave = async () => {
    if (!draft.id.trim()) {
      setErrorMsg('Setting key is required');
      return;
    }
    setActionLoading(true);
    setErrorMsg('');
    try {
      const value = parseValue(draft.value, draft.type);
      await writeAdminConfig({
        collection: 'appSettings',
        docId: draft.id.trim(),
        updates: {
          value,
          type: draft.type,
          description: draft.description || '',
        },
      });
      setShowEdit(false);
    } catch (err) {
      console.error(err);
      setErrorMsg(`Save failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete setting "${id}"?`)) return;
    setActionLoading(true);
    try {
      await deleteAdminConfig({ collection: 'appSettings', docId: id });
    } catch (err) {
      console.error(err);
      setErrorMsg(`Delete failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuickToggle = async (setting) => {
    if (setting.type !== 'boolean') return;
    setActionLoading(true);
    try {
      await writeAdminConfig({
        collection: 'appSettings',
        docId: setting.id,
        updates: { value: !setting.value },
      });
    } catch (err) {
      console.error(err);
      setErrorMsg(`Toggle failed: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>App Settings</h1>
          {!loading && <Badge variant="primary">{settings.length} settings</Badge>}
        </div>
        <p className={styles.subtitle}>
          Feature flags & runtime configuration
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {error && (
        <Card>
          <div style={{ color: '#f87171' }}>Error loading settings: {error.message}</div>
        </Card>
      )}
      {errorMsg && (
        <Card>
          <div className={styles.errorBox}>
            <FiAlertCircle />
            <span>{errorMsg}</span>
          </div>
        </Card>
      )}

      <Card
        title="Settings"
        subtitle="Click a flag to toggle, or edit to change value"
        action={
          <Button variant="primary" icon={<FiPlus />} onClick={openCreate}>
            New Setting
          </Button>
        }
      >
        {loading ? (
          <div className={styles.empty}>Loading…</div>
        ) : settings.length === 0 ? (
          <div className={styles.empty}>
            <p>No settings yet.</p>
            <p style={{ color: '#666', fontSize: 12 }}>
              Create one with a key like <code>maintenanceMode</code>, type <code>boolean</code>, value{' '}
              <code>false</code>.
            </p>
          </div>
        ) : (
          <div className={styles.list}>
            {settings.map((s) => (
              <div key={s.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.key}>
                    <code>{s.id}</code>
                    <Badge variant="secondary">{s.type || 'string'}</Badge>
                  </div>
                  {s.description && <div className={styles.desc}>{s.description}</div>}
                </div>

                <div className={styles.rowRight}>
                  {s.type === 'boolean' ? (
                    <button
                      className={`${styles.toggle} ${s.value ? styles.toggleOn : ''}`}
                      onClick={() => handleQuickToggle(s)}
                      disabled={actionLoading}
                      aria-label={s.value ? 'on' : 'off'}
                    >
                      <span className={styles.knob} />
                    </button>
                  ) : (
                    <code className={styles.valueDisplay}>{valueDisplay(s)}</code>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)}>
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={<FiTrash2 />}
                    onClick={() => handleDelete(s.id)}
                  />
                </div>

                <div className={styles.timestamp}>updated {formatDate(s.updatedAt)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        title={editingId ? `Edit ${editingId}` : 'New Setting'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button variant="primary" icon={<FiSave />} onClick={handleSave} loading={actionLoading}>
              Save
            </Button>
          </>
        }
      >
        <div className={styles.form}>
          {errorMsg && <div className={styles.errorBox}><FiAlertCircle /><span>{errorMsg}</span></div>}

          {!editingId && availableSuggestions.length > 0 && (
            <div>
              <label className={styles.label}>
                Pick a suggested setting (or type a custom key below)
              </label>
              <div className={styles.suggestions}>
                {availableSuggestions.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={styles.suggestionBtn}
                    onClick={() => applySuggestion(s)}
                    title={s.description}
                  >
                    <code>{s.key}</code>
                    <span className={styles.suggestionType}>{s.type}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <Input
            label="Key"
            value={draft.id}
            onChange={(e) => setDraft({ ...draft, id: e.target.value })}
            disabled={!!editingId}
            placeholder="e.g. maintenanceMode (camelCase, no spaces)"
          />
          <div>
            <label className={styles.label}>Type</label>
            <select
              className={styles.select}
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {draft.type === 'boolean' ? (
            <div>
              <label className={styles.label}>Value</label>
              <select
                className={styles.select}
                value={draft.value}
                onChange={(e) => setDraft({ ...draft, value: e.target.value })}
              >
                <option value="false">false</option>
                <option value="true">true</option>
              </select>
            </div>
          ) : draft.type === 'json' ? (
            <div>
              <label className={styles.label}>JSON Value</label>
              <textarea
                className={styles.textArea}
                value={draft.value}
                onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                rows={6}
                placeholder='{"feature": "enabled", "rolloutPercent": 50}'
              />
            </div>
          ) : (
            <Input
              label="Value"
              type={draft.type === 'number' ? 'number' : 'text'}
              value={draft.value}
              onChange={(e) => setDraft({ ...draft, value: e.target.value })}
            />
          )}

          <Input
            label="Description (optional)"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            placeholder="What does this flag control?"
          />
        </div>
      </Modal>
    </div>
  );
};

export default AppSettings;
